import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Users, UserCircle, Briefcase } from 'lucide-react';
import { useCrud } from '../../hooks/useApi';
import { useLookupMaps } from '../../hooks/useLookupMaps';
import { institutionService } from '../../services/api';
import EntityAutocomplete from '../../components/Common/EntityAutocomplete';
import EmptyState from '../../components/Common/EmptyState';
import FormModalLayout from '../../components/Common/FormModalLayout';
import FormPageShell from '../../components/Common/FormPageShell';
import FormSection from '../../components/Common/FormSection';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { useTranslation } from 'react-i18next';

const PERFIL_LABELS = { 1: 'Gestão', 2: 'Técnico', 3: 'Administrador' };

const Servidores = () => {
  const { t } = useTranslation('institution');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ search: '', setorId: '', perfil: '' });
  const [personId, setPersonId] = useState('');
  const { personLabel, personOptions, personMap } = useLookupMaps({ persons: true });

  const { useList: useSetoresList } = useCrud('setores', institutionService.setorCrud);
  const { useList: useCargosList } = useCrud('cargos', institutionService.cargoCrud);
  const { data: setoresData } = useSetoresList();
  const { data: cargosData } = useCargosList();
  const setores = Array.isArray(setoresData) ? setoresData : setoresData?.data ?? [];
  const cargos = Array.isArray(cargosData) ? cargosData : cargosData?.data ?? [];

  const { useList, useCreate, useUpdate, useDelete } = useCrud('servidores', institutionService.servidorCrud);
  const { data, isLoading, error } = useList();
  const list = Array.isArray(data) ? data : data?.data ?? [];
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const filtered = list.filter((s) => {
    const matchSetor = !filters.setorId || s.setor_id === filters.setorId;
    const matchPerfil = !filters.perfil || String(s.perfil) === String(filters.perfil);
    const personName = personMap[s.person_id] || '';
    const matchSearch =
      !filters.search ||
      (s.matricula && s.matricula.includes(filters.search)) ||
      personName.toLowerCase().includes(filters.search.toLowerCase()) ||
      (s.person_id && s.person_id.includes(filters.search));
    return matchSetor && matchPerfil && matchSearch;
  });

  const getSetorName = (id) => setores.find((s) => s.id === id)?.name || id;
  const getCargoName = (id) => cargos.find((c) => c.id === id)?.name || id;

  const handleSubmit = (formData) => {
    if (!formData.person_id) {
      toast.error('Selecione a pessoa');
      return;
    }
    if (editing) updateMutation.mutate({ id: editing.id, data: formData });
    else createMutation.mutate(formData);
    setShowModal(false);
    setEditing(null);
  };

  if (error)
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("servidoresTitle")}
          description="Vínculo entre pessoa (identity), setor, cargo e perfil de acesso."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Instituição', to: '/institution/staff' },
            { label: 'Servidores' },
          ]}
        >
          <Can permission={P.INSTITUTION_ACCESS}>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setPersonId('');
                setShowModal(true);
              }}
              className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" /> Novo servidor
            </button>
          </Can>
        </PageHeader>
      <div className="data-list-card p-5 sm:p-6">
        <div className="mb-4 flex gap-4 flex-wrap">
          <input type="text" placeholder="Buscar (nome ou matrícula)..." value={filters.search || ''} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="form-input max-w-xs" />
          <select value={filters.setorId || ''} onChange={(e) => setFilters({ ...filters, setorId: e.target.value })} className="form-select max-w-xs">
            <option value="">Todos os setores</option>
            {setores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={filters.perfil || ''} onChange={(e) => setFilters({ ...filters, perfil: e.target.value })} className="form-select max-w-xs">
            <option value="">Todos os perfis</option>
            {[1, 2, 3].map((p) => <option key={p} value={p}>{PERFIL_LABELS[p]}</option>)}
          </select>
        </div>
        {isLoading ? <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div> : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pessoa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Setor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cargo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matrícula</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Perfil</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{personLabel(s.person_id)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{getSetorName(s.setor_id)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{getCargoName(s.cargo_id)}</td>
                    <td className="px-6 py-4 text-sm">{s.matricula || '—'}</td>
                    <td className="px-6 py-4 text-sm"><span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{PERFIL_LABELS[s.perfil] || s.perfil}</span></td>
                    <td className="px-6 py-4 text-right">
                      <Can permission={P.INSTITUTION_ACCESS}>
                        <button type="button" onClick={() => { setEditing(s); setPersonId(s.person_id || ''); setShowModal(true); }} className="text-primary-600 hover:text-primary-900 p-1 mr-2"><Edit className="w-4 h-4 inline" /></button>
                        <button type="button" onClick={() => window.confirm('Excluir?') && deleteMutation.mutate(s.id)} className="text-red-600 hover:text-red-900 p-1"><Trash2 className="w-4 h-4 inline" /></button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <EmptyState
                icon={Users}
                title={t("servidoresEmpty")}
                description="Vincule pessoas a setores e cargos ou altere os filtros."
                action={
                  <Can permission={P.INSTITUTION_ACCESS}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setEditing(null);
                        setPersonId('');
                        setShowModal(true);
                      }}
                    >
                      Novo servidor
                    </button>
                  </Can>
                }
              />
            )}
          </div>
        )}
      </div>
      </div>
      {showModal && (
        <FormModalLayout
          title={editing ? 'Editar servidor' : 'Novo servidor'}
          subtitle="Vínculo entre pessoa (identity), setor, cargo e perfil de acesso."
          headerIcon={Users}
          historyEntityType="staff"
          historyEntityId={editing?.id}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
            setPersonId('');
          }}
          size="lg"
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                  setPersonId('');
                }}
                className="btn btn-outline"
              >
                Cancelar
              </button>
              <Can permission={P.INSTITUTION_ACCESS}>
                <button type="submit" form="inst-servidor-form" className="btn btn-primary">
                  Salvar
                </button>
              </Can>
            </>
          }
        >
          <form
            id="inst-servidor-form"
            key={editing?.id ?? 'new'}
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              handleSubmit({
                person_id: personId || '',
                setor_id: form.setorId?.value || '',
                cargo_id: form.cargoId?.value || '',
                matricula: form.matricula?.value || undefined,
                vinculo: form.vinculo?.value || undefined,
                perfil: parseInt(form.perfil?.value || '1', 10),
                receive_routing: form.receiveRouting?.checked || false,
              });
            }}
            className="space-y-6"
          >
            <FormSection
              id="srv-sec-id"
              title={t("common:identify")}
              description="Pessoa cadastrada no serviço de identidade."
              icon={UserCircle}
              variant="primary"
            >
              <div className="form-group">
                <EntityAutocomplete
                  id="inst-srv-person"
                  label="Pessoa"
                  value={personId}
                  onChange={setPersonId}
                  options={personOptions}
                  placeholder="Buscar pelo nome…"
                  required
                />
              </div>
            </FormSection>
            <FormSection
              id="srv-sec-lot"
              title={t("posting")}
              description="Setor, cargo, matrícula e perfil."
              icon={Briefcase}
              variant="slate"
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="form-group">
                  <label htmlFor="inst-srv-setor" className="form-label form-label-required">
                    Setor
                  </label>
                  <select id="inst-srv-setor" name="setorId" defaultValue={editing?.setor_id} className="form-select" required>
                    <option value="">Selecione o setor</option>
                    {setores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="inst-srv-cargo" className="form-label form-label-required">
                    Cargo
                  </label>
                  <select id="inst-srv-cargo" name="cargoId" defaultValue={editing?.cargo_id} className="form-select" required>
                    <option value="">Selecione o cargo</option>
                    {cargos.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="inst-srv-mat" className="form-label">
                    Matrícula
                  </label>
                  <input id="inst-srv-mat" name="matricula" defaultValue={editing?.matricula} className="form-input" />
                </div>
                <div className="form-group">
                  <label htmlFor="inst-srv-vinc" className="form-label">
                    Vínculo
                  </label>
                  <input id="inst-srv-vinc" name="vinculo" defaultValue={editing?.vinculo} className="form-input" />
                </div>
                <div className="form-group md:col-span-2">
                  <label htmlFor="inst-srv-perfil" className="form-label">
                    Perfil
                  </label>
                  <select id="inst-srv-perfil" name="perfil" defaultValue={editing?.perfil ?? 1} className="form-select max-w-md">
                    {[1, 2, 3].map((p) => (
                      <option key={p} value={p}>
                        {PERFIL_LABELS[p]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </FormSection>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5 text-sm text-gray-700">
              <input name="receiveRouting" type="checkbox" defaultChecked={editing?.receive_routing} className="form-check" />
              Receber tramitação
            </label>
          </form>
        </FormModalLayout>
      )}
    </FormPageShell>
  );
};

export default Servidores;
